import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faInfoCircle, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';

const JobInterviewConversation = () => {
  const [conversationData, setConversationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [emotionalState, setEmotionalState] = useState('neutral1');
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [bodyLanguageAnalysis, setBodyLanguageAnalysis] = useState([]);
  const [isCameraCollapsed, setIsCameraCollapsed] = useState(false);
  
  const chatContainerRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const captureIntervalRef = useRef(null);
  const lastCaptureTimeRef = useRef(0);
  const navigate = useNavigate();
  
  // Fetch conversation data on component mount
  useEffect(() => {
    const fetchConversationData = async () => {
      try {
        const response = await axios.get('/interview/job_interview_conversation_react', {
          withCredentials: true
        });
        setConversationData(response.data);
        
        // Add initial greeting if no messages
        if (!response.data.conversation_history || response.data.conversation_history === "No messages found for this conversation.") {
          setMessages([{
            role: 'assistant',
            content: `Welcome to your job interview. I'm Jacob Smith from HR. Could you start by introducing yourself and telling me why you're interested in this position?`
          }]);
        } else {
          // Parse conversation history
          const history = response.data.conversation_history.split('\n').map(line => {
            const match = line.match(/\[(.*?)\] (.*?): (.*)/);
            if (match) {
              const [_, timestamp, speaker, content] = match;
              return {
                timestamp,
                role: speaker.toLowerCase() === 'panel' || speaker.toLowerCase() === 'assistant' ? 'assistant' : 'user',
                content
              };
            }
            return null;
          }).filter(Boolean);
          
          setMessages(history);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching conversation data:', err);
        setError('Failed to load interview data. Please try again.');
        setLoading(false);
        
        // Redirect to home if unauthorized
        if (err.response?.status === 401) {
          navigate('/');
        }
      }
    };
    
    fetchConversationData();
  }, [navigate]);
  
  // Scroll to bottom of chat when messages change
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);
  
  // Setup video capture when camera is enabled/disabled
  useEffect(() => {
    async function setupVideo() {
      if (cameraEnabled) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 640 },
              height: { ideal: 480 },
              facingMode: 'user'
            }
          });
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            streamRef.current = stream;
            
            // Start capturing frames every 3 seconds
            captureIntervalRef.current = setInterval(captureFrame, 3000);
          }
        } catch (err) {
          console.error('Error accessing camera:', err);
          alert('Failed to access camera. Please check your permissions and try again.');
          setCameraEnabled(false);
        }
      } else {
        // Clean up when camera is disabled
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
        
        if (captureIntervalRef.current) {
          clearInterval(captureIntervalRef.current);
          captureIntervalRef.current = null;
        }
        
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }
    }
    
    setupVideo();
    
    // Cleanup on component unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      if (captureIntervalRef.current) {
        clearInterval(captureIntervalRef.current);
      }
    };
  }, [cameraEnabled]);
  
  // Function to capture a frame from the video
  const captureFrame = useCallback(() => {
    if (!streamRef.current || !videoRef.current || !canvasRef.current || 
        videoRef.current.readyState !== videoRef.current.HAVE_ENOUGH_DATA) {
      return;
    }
    
    const now = Date.now();
    // Only capture if at least 3 seconds have passed
    if (now - lastCaptureTimeRef.current < 3000) {
      return;
    }
    
    const canvas = canvasRef.current;
    const video = videoRef.current;
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw the current video frame on the canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convert canvas to base64 image
    const imageData = canvas.toDataURL('image/jpeg', 0.7);
    
    // Send the image for analysis
    analyzeBodyLanguage(imageData);
    
    lastCaptureTimeRef.current = now;
  }, []);
  
  // Function to send the image for body language analysis
  const analyzeBodyLanguage = useCallback(async (imageData) => {
    if (!conversationData || !conversationData.conversation_id) return;
    
    try {
      const response = await axios.post('/interview/analyze_body_language', {
        image_data: imageData,
        conversation_id: conversationData.conversation_id
      }, {
        withCredentials: true
      });
      
      setBodyLanguageAnalysis(prev => [...prev, response.data.analysis]);
      console.log('Body language analysis:', response.data.analysis);
    } catch (err) {
      console.error('Error analyzing body language:', err);
    }
  }, [conversationData]);
  
  // Modified submit handler to include body language analysis
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isSubmitting) return;
    
    const newMessage = {
      role: 'user',
      content: inputMessage
    };
    
    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsSubmitting(true);
    
    try {
      // Get the latest body language analysis if available from our state
      // The backend will retrieve from database if none is provided and camera was previously enabled
      const latestAnalysis = bodyLanguageAnalysis.length > 0 
        ? bodyLanguageAnalysis[bodyLanguageAnalysis.length - 1] 
        : null;
      
      const response = await axios.post('/interview/job_interview_message', {
        conversation_id: conversationData.conversation_id,
        message: inputMessage,
        body_language_analysis: latestAnalysis
      }, {
        withCredentials: true
      });
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
      
      // Update emotional state if provided
      if (response.data.emotional_state) {
        setEmotionalState(response.data.emotional_state);
      }
      
      // Reset body language analysis after using it
      setBodyLanguageAnalysis([]);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Modified feedback handler to include body language analysis
  const handleGetFeedback = async () => {
    setIsFeedbackLoading(true);
    
    try {
      const response = await axios.post('/interview/generate_interview_feedback', {
        conversation_id: conversationData.conversation_id,
        include_body_language: cameraEnabled
      }, {
        withCredentials: true
      });
      
      setFeedback(response.data.feedback);
      
      // Disable camera after generating feedback
      if (cameraEnabled) {
        setCameraEnabled(false);
      }
    } catch (err) {
      console.error('Error generating feedback:', err);
      let errorMessage = 'Failed to generate feedback. Please try again.';
      
      if (err.response) {
        // Server responded with error status
        const serverError = err.response.data?.error || 'Server error occurred';
        errorMessage = `Server error: ${serverError}`;
      } else if (err.request) {
        // Network error
        errorMessage = 'Network error: Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setIsFeedbackLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col-12 text-center">
          <h1 className="mb-3">Job Interview Simulation</h1>
          <p className="lead">Interview with Jacob Smith, HR Professional</p>
        </div>
      </div>

      <div className="row">
        <div className="col-md-8">
          <div className="persona-image-container mb-4 text-center">
            <img 
              src={`/static/images/personas/job/${emotionalState}.png`}
              alt="Jacob Smith" 
              className="img-fluid rounded shadow-sm"
              style={{ maxWidth: '100%', maxHeight: '300px' }}
              onError={(e) => {
                console.warn(`Image not found: ${e.target.src}, falling back to neutral1`);
                e.target.src = '/static/images/personas/job/neutral1.png';
              }}
            />
          </div>
          
          <div className="card mb-4">
            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Camera</h5>
              <div className="d-flex align-items-center">
                <div className="form-check form-switch me-2">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="camera-toggle"
                    checked={cameraEnabled}
                    onChange={(e) => setCameraEnabled(e.target.checked)}
                  />
                  <label className="form-check-label text-white" htmlFor="camera-toggle">
                    Enable
                  </label>
                </div>
                <button 
                  className="btn btn-sm btn-outline-light" 
                  type="button"
                  onClick={() => setIsCameraCollapsed(!isCameraCollapsed)}
                >
                  <FontAwesomeIcon icon={isCameraCollapsed ? faChevronDown : faChevronUp} />
                </button>
              </div>
            </div>
            <div className={`collapse ${isCameraCollapsed ? '' : 'show'}`}>
              <div className="card-body">
                {cameraEnabled ? (
                  <div className="video-container">
                    <video 
                      ref={videoRef}
                      className="w-100 rounded" 
                      autoPlay 
                      muted
                      playsInline
                    />
                    <canvas 
                      ref={canvasRef}
                      style={{ display: 'none' }}
                    />
                    <div className="mt-2">
                      <div className="alert alert-info small" role="alert">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" /> 
                        Your camera is active. Snapshots are being analyzed to provide body language feedback.
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="camera-placeholder text-center py-4">
                    <FontAwesomeIcon icon={faVideo} className="fa-3x text-muted mb-3" />
                    <p className="text-muted">Enable camera to receive body language feedback</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">Position Description</h5>
            </div>
            <div className="card-body">
              <div className="position-description">
                <p>{conversationData?.position_description}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header bg-success text-white">
              <h5 className="mb-0">Key Topics</h5>
            </div>
            <div className="card-body">
              <div className="interview-topics">
                <ul>
                  {conversationData?.topics?.topics ? (
                    conversationData.topics.topics.map((topic, index) => (
                      <li key={index}>{topic}</li>
                    ))
                  ) : (
                    <li>No topics available</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card mb-4">
            <div className="card-header bg-dark text-white d-flex align-items-center">
              <div className="jacob-avatar">J</div>
              <h5 className="mb-0">Jacob Smith - Interview Panel</h5>
            </div>
            <div className="card-body">
              <div 
                className="chat-container d-flex flex-column"
                ref={chatContainerRef} 
                style={{
                  height: '60vh',
                  overflowY: 'auto',
                  border: '1px solid #dee2e6',
                  borderRadius: '0.25rem',
                  padding: '1rem',
                  backgroundColor: '#fff'
                }}
              >
                {messages.map((message, index) => (
                  <div 
                    key={index} 
                    className={`message ${message.role === 'user' ? 'user-message' : 'jacob-message'}`}
                    style={{
                      backgroundColor: message.role === 'user' ? '#007bff' : '#e9ecef',
                      color: message.role === 'user' ? 'white' : '#212529',
                      borderRadius: message.role === 'user' ? '1rem 1rem 0 1rem' : '1rem 1rem 1rem 0',
                      padding: '0.75rem 1rem',
                      marginBottom: '1rem',
                      maxWidth: '80%',
                      alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                      marginLeft: message.role === 'user' ? 'auto' : '0'
                    }}
                  >
                    {message.content}
                  </div>
                ))}
                
                {isSubmitting && (
                  <div className="typing-indicator d-flex align-items-center ms-2 mb-3">
                    <div className="dot-flashing"></div>
                    <span className="ms-2 text-muted">Jacob is typing...</span>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit} className="mt-3">
                <div className="input-group">
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Type your response..." 
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    disabled={isSubmitting}
                  />
                  <button 
                    className="btn btn-primary" 
                    type="submit"
                    disabled={isSubmitting || !inputMessage.trim()}
                  >
                    <i className="fas fa-paper-plane"></i> Send
                  </button>
                </div>
              </form>

              <div className="d-grid gap-2 mt-3">
                <button 
                  className="btn btn-success" 
                  onClick={handleGetFeedback}
                  disabled={isFeedbackLoading || feedback}
                >
                  {isFeedbackLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Generating feedback...
                    </>
                  ) : feedback ? (
                    <>
                      <i className="fas fa-check-circle"></i> Feedback Generated
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check-circle"></i> End Interview & Get Feedback
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {feedback && (
            <div className="card">
              <div className="card-header bg-info text-white">
                <h5 className="mb-0">Interview Feedback</h5>
              </div>
              <div className="card-body">
                <div dangerouslySetInnerHTML={{ __html: feedback }} />
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .jacob-avatar {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          background-color: #6c757d;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: bold;
          margin-right: 1rem;
        }
        
        .position-description {
          background-color: #f8f9fa;
          border-left: 4px solid #007bff;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        
        .interview-topics {
          background-color: #f8f9fa;
          border-left: 4px solid #28a745;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        
        .dot-flashing {
          position: relative;
          width: 10px;
          height: 10px;
          border-radius: 5px;
          background-color: #9880ff;
          color: #9880ff;
          animation: dot-flashing 1s infinite linear alternate;
          animation-delay: 0.5s;
        }
        .dot-flashing::before, .dot-flashing::after {
          content: '';
          display: inline-block;
          position: absolute;
          top: 0;
        }
        .dot-flashing::before {
          left: -15px;
          width: 10px;
          height: 10px;
          border-radius: 5px;
          background-color: #9880ff;
          color: #9880ff;
          animation: dot-flashing 1s infinite alternate;
          animation-delay: 0s;
        }
        .dot-flashing::after {
          left: 15px;
          width: 10px;
          height: 10px;
          border-radius: 5px;
          background-color: #9880ff;
          color: #9880ff;
          animation: dot-flashing 1s infinite alternate;
          animation-delay: 1s;
        }

        @keyframes dot-flashing {
          0% {
            background-color: #9880ff;
          }
          50%, 100% {
            background-color: rgba(152, 128, 255, 0.2);
          }
        }
        
        /* New styles for improved layout */
        .persona-image-container {
          text-align: center;
          margin-bottom: 1.5rem;
        }
        
        .persona-image-container img {
          max-height: 350px;
          object-fit: contain;
          box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
          transition: transform 0.3s ease;
        }
        
        .btn-outline-light:focus {
          box-shadow: none !important;
        }
        
        /* Responsive adjustments */
        @media (max-width: 767.98px) {
          .row {
            flex-direction: column;
          }
          .col-md-8, .col-md-4 {
            width: 100%;
          }
          .chat-container {
            height: 40vh !important;
          }
        }
      `}</style>
    </div>
  );
};

export default JobInterviewConversation; 